import { merge, createMetadataProvider, createChatProvider, createModelProvider, createEmbedProvider, createSpeechProvider, createTranscriptionProvider, createImageProvider } from '@xsai-ext/shared-providers';
import { objCamelToSnake } from '@xsai/shared';

const createAnthropic = (apiKey, baseURL = "https://api.anthropic.com/v1/") => merge(
  createMetadataProvider("anthropic"),
  /** @see {@link https://docs.anthropic.com/en/docs/about-claude/models/all-models} */
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createAzure = async (options) => {
  const headers = typeof options.apiKey === "string" ? { "api-key": options.apiKey } : void 0;
  const baseURL = `https://${options.resourceName}.services.ai.azure.com/models/`;
  const fetch = async (input, init) => {
    if (options.apiVersion != null)
      input.searchParams.set("api-version", options.apiVersion);
    const token = `Bearer ${typeof options.apiKey === "function" ? await options.apiKey() : options.apiKey}`;
    init.headers ??= {};
    if (Array.isArray(init.headers))
      init.headers.push(["Authorization", token]);
    else if (init.headers instanceof Headers)
      init.headers.append("Authorization", token);
    else
      init.headers.Authorization = token;
    return globalThis.fetch(input, init);
  };
  return merge(
    createMetadataProvider("azure"),
    createChatProvider({ baseURL, fetch, headers }),
    createEmbedProvider({ baseURL, fetch, headers }),
    createSpeechProvider({ baseURL, fetch, headers }),
    createTranscriptionProvider({ baseURL, fetch, headers }),
    createModelProvider({ baseURL, fetch, headers })
  );
};

const createCerebras = (apiKey, baseURL = "https://api.cerebras.ai/v1/") => merge(
  createMetadataProvider("cerebras"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createDeepInfra = (apiKey, baseURL = "https://api.deepinfra.com/v1/openai/") => merge(
  createMetadataProvider("deepinfra"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL })
);

const createDeepSeek = (apiKey, baseURL = "https://api.deepseek.com/") => merge(
  createMetadataProvider("deepseek"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createFatherless = (apiKey, baseURL = "https://api.featherless.ai/v1/") => merge(
  createMetadataProvider("fatherless-ai"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({
    apiKey,
    baseURL,
    fetch: async (...args) => globalThis.fetch(...args).then(async (res) => res.json()).then((data) => Response.json({ data, object: "list" }))
  })
);

const createFireworks = (apiKey, baseURL = "https://api.fireworks.ai/inference/v1/") => merge(
  createMetadataProvider("fireworks"),
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createGoogleGenerativeAI = (apiKey, baseURL = "https://generativelanguage.googleapis.com/v1beta/openai/") => merge(
  createMetadataProvider("google-generative-ai"),
  /** @see {@link https://aistudio.google.com/u/1/prompts/new_chat} */
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createGroq = (apiKey, baseURL = "https://api.groq.com/openai/v1/") => merge(
  createMetadataProvider("groq"),
  /** @see {@link https://console.groq.com/docs/models} */
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL }),
  createTranscriptionProvider({ apiKey, baseURL })
);

const createMinimaxi = (apiKey, baseURL = "https://api.minimaxi.chat/v1/") => merge(
  createMetadataProvider("minimaxi"),
  /** @see {@link https://www.minimax.io/platform/document/ChatCompletion%20v2?key=66701d281d57f38758d581d0#QklxsNSbaf6kM4j6wjO5eEek} */
  createChatProvider({ apiKey, baseURL })
);
const createMinimax = (apiKey, baseURL = "https://api.minimax.chat/v1/") => merge(
  createMetadataProvider("minimax"),
  /** @see {@link https://platform.minimaxi.com/document/ChatCompletion%20v2?key=66701d281d57f38758d581d0#1XspWaYA7baUnFix0otJIQkt} */
  createChatProvider({ apiKey, baseURL })
);

const createMistral = (apiKey, baseURL = "https://api.mistral.ai/v1/") => merge(
  createMetadataProvider("mistral"),
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createMoonshot = (apiKey, baseURL = "https://api.moonshot.cn/v1/") => merge(
  createMetadataProvider("moonshot"),
  /** @see {@link https://platform.moonshot.cn/docs/api/chat#%E5%85%AC%E5%BC%80%E7%9A%84%E6%9C%8D%E5%8A%A1%E5%9C%B0%E5%9D%80} */
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createNovita = (apiKey, baseURL = "https://api.novita.ai/v3/openai/") => merge(
  createMetadataProvider("novita"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createOpenAI = (apiKey, baseURL = "https://api.openai.com/v1/") => merge(
  createMetadataProvider("openai"),
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createImageProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL }),
  createSpeechProvider({ apiKey, baseURL }),
  createTranscriptionProvider({ apiKey, baseURL })
);

const createOpenRouter = (apiKey, baseURL = "https://openrouter.ai/api/v1/") => merge(
  createMetadataProvider("openrouter"),
  {
    chat: (model, openRouterOptions) => {
      const requestOptions = { apiKey, baseURL, model };
      const toOpenRouterOptions = ({ extraHeaders, models, provider }) => {
        if (extraHeaders != null) {
          requestOptions.headers ??= {};
          Object.assign(requestOptions.headers, extraHeaders);
        }
        let transformedProvider;
        if (provider != null) {
          transformedProvider = objCamelToSnake(provider);
        }
        return objCamelToSnake({
          models,
          provider: transformedProvider
        });
      };
      return {
        ...openRouterOptions ? toOpenRouterOptions(openRouterOptions) : {},
        ...requestOptions
      };
    }
  },
  createModelProvider({ apiKey, baseURL })
);

const createPerplexity = (apiKey, baseURL = "https://api.perplexity.ai/") => merge(
  createMetadataProvider("perplexity"),
  /** @see {@link https://docs.perplexity.ai/api-reference/chat-completions} */
  createChatProvider({ apiKey, baseURL })
);

const createQwen = (apiKey, baseURL = "https://dashscope.aliyuncs.com/compatible-mode/v1/") => merge(
  createMetadataProvider("qwen"),
  /** @see {@link https://help.aliyun.com/zh/model-studio/getting-started/models} */
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createSiliconFlow = (apiKey, baseURL = "https://api.siliconflow.cn/v1/") => merge(
  createMetadataProvider("silicon-flow"),
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL }),
  createSpeechProvider({ apiKey, baseURL }),
  createTranscriptionProvider({ apiKey, baseURL })
);

const createStepfun = (apiKey, baseURL = "https://api.stepfun.com/v1/") => merge(
  createMetadataProvider("stepfun"),
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL }),
  createSpeechProvider({ apiKey, baseURL }),
  createTranscriptionProvider({ apiKey, baseURL })
);

const createTencentHunyuan = (apiKey, baseURL = "https://api.hunyuan.cloud.tencent.com/v1/") => merge(
  createMetadataProvider("tencent-hunyuan"),
  /** @see {@link https://cloud.tencent.com/document/product/1729/111007} */
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL })
);

const createTogetherAI = (apiKey, baseURL = "https://api.together.xyz/v1/") => merge(
  createMetadataProvider("together-ai"),
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({
    apiKey,
    baseURL,
    fetch: async (...args) => globalThis.fetch(...args).then(async (res) => res.json()).then((data) => Response.json({ data, object: "list" }))
  })
);

const createWorkersAI = (apiKey, accountId) => {
  const baseURL = `https://api.cloudflare.com/client/v4/accounts/${accountId}/ai/v1/`;
  return merge(
    createMetadataProvider("workers-ai"),
    createChatProvider({ apiKey, baseURL }),
    createEmbedProvider({ apiKey, baseURL })
  );
};

const createXAI = (apiKey, baseURL = "https://api.x.ai/v1/") => merge(
  createMetadataProvider("xai"),
  /** @see {@link https://docs.x.ai/docs/models?cluster=us-east-1} */
  createChatProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

const createZhipu = (apiKey, baseURL = "https://open.bigmodel.cn/api/paas/v4/") => merge(
  createMetadataProvider("zhipu"),
  /** @see {@link https://open.bigmodel.cn/dev/api/thirdparty-frame/openai-sdk} */
  createChatProvider({ apiKey, baseURL }),
  createEmbedProvider({ apiKey, baseURL }),
  createModelProvider({ apiKey, baseURL })
);

export { createAnthropic, createAzure, createCerebras, createDeepInfra, createDeepSeek, createFatherless, createFireworks, createGoogleGenerativeAI, createGroq, createMinimax, createMinimaxi, createMistral, createMoonshot, createNovita, createOpenAI, createOpenRouter, createPerplexity, createQwen, createSiliconFlow, createStepfun, createTencentHunyuan, createTogetherAI, createWorkersAI, createXAI, createZhipu };
